/**
 * <DeviceCheckRow> — une ligne du test du matériel avant le live (molecule).
 *
 * L'ancienne `StepRow` : la pastille de l'appareil, son nom, et sa phrase d'état, qu'elle garde EN
 * PERMANENCE (« On t'entend », « Connexion rapide ») — une étape franchie qui se replierait sur
 * son seul nom ferait perdre de vue ce qui a été vérifié. La couleur du verdict ne teint que la
 * pastille : le reste de la ligne garde ses encres, pour que rien ne vire au rouge d'un coup.
 *
 * `muted` : une ligne repliée, plus petite, son nom à l'encre secondaire. La ligne de l'étape en
 * cours la garde pleine. `end` : ce qui s'accroche au bout de la ligne de titre (« Réessayer »).
 *
 * Composes DS pieces only — `<IconBubble>`, `<Text>`.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { IconBubble } from "../../atoms/icons/IconBubble"
import { type IconName } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import {
    type UIDeviceCheckLine,
    type UIDeviceCheckStepId,
    type UIDeviceCheckVerdict,
} from "../../../ports/primary/ui/types/studentDeviceCheck"

// Les étapes du test et leur verdict : leurs formes vivent dans `ports/primary/ui/types` (socle du live).
export type { UIDeviceCheckLine, UIDeviceCheckStepId, UIDeviceCheckVerdict }

/** Le glyphe de chaque appareil : ceux d'aujourd'hui. */
export const DEVICE_CHECK_ICONS: Record<UIDeviceCheckStepId, IconName> = {
    network: "wifi",
    microphone: "microphone",
    speaker: "volume-up",
    camera: "video",
}

/** La pastille d'un verdict : sa tinte et son encre. */
export const VERDICT_TONE: Record<UIDeviceCheckVerdict, { fill: SemanticColorKey; ink: SemanticColorKey }> = {
    ok: { fill: "successLight", ink: "success-strong" },
    warn: { fill: "warningLight", ink: "warning-strong" },
    bad: { fill: "dangerLight", ink: "danger-strong" },
    // Pas encore mesuré : un gris qui se lit sur la carte comme sur la lavande de l'étape en cours.
    pending: { fill: "border-subtle", ink: "text-muted" },
}

export type DeviceCheckRowProps = {
    line: UIDeviceCheckLine
    /** Une ligne repliée : plus petite, son nom à l'encre secondaire. Défaut `false`. */
    muted?: boolean
    /** Accroché au bout de la ligne de titre — « Réessayer ». */
    end?: React.ReactNode
    testID?: string
}

/** La pastille d'une étape : la colonne sur laquelle le contenu d'une étape s'aligne. */
export const DEVICE_CHECK_BUBBLE = 32

export function DeviceCheckRow(props: DeviceCheckRowProps) {
    const { line, muted = false, end, testID } = props
    const tone = VERDICT_TONE[line.skipped === true ? "bad" : line.verdict]

    return (
        <View style={styles.row} testID={testID}>
            <IconBubble
                iconName={DEVICE_CHECK_ICONS[line.id]}
                size={DEVICE_CHECK_BUBBLE}
                iconSize={16}
                color={tone.fill}
                iconColor={tone.ink}
            />
            <View style={styles.texts}>
                <Text size={muted ? "text-md" : "text-lg"} weight="bold" color={muted ? "text-muted" : "text-strong"}>
                    {line.label}
                </Text>
                {line.detail !== "" && (
                    <Text size="text-sm" color="text-muted">
                        {line.detail}
                    </Text>
                )}
            </View>
            {end}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    // Accrochées à la ligne de titre : la pastille et le bout de ligne ne descendent pas quand la
    // phrase d'état passe sur deux lignes.
    row: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: theme.spacing["3"],
    },
    texts: {
        flex: 1,
        gap: theme.spacing["1"],
    },
}))
